import { tenantControllerGetTenantSales } from '@/generated/korza-api-client';
import type { PaginatedSalesResponseDto } from '@/generated/korza-api-client/types.gen';
import {
  type InfiniteData,
  useInfiniteQuery,
  useQuery,
} from '@tanstack/react-query';
import { useTenant } from './use-tenant';
import { PaginatedSales } from '@/lib/dtos'; // Import PaginatedSales DTO
import {
  buildSalesQuery,
  getNextSalesPage,
  SALES_MOBILE_PAGE_SIZE,
  type SalesHistoryFilterValues,
} from '@/lib/sales-history';

export function useSales(
  page: number = 1,
  limit: number = 10,
  filters: SalesHistoryFilterValues
) {
  const tenant = useTenant();

  return useQuery<PaginatedSales, Error>({
    // Add explicit types for useQuery
    queryKey: ['sales', tenant.slug, page, limit, filters],
    queryFn: () => {
      if (!tenant.slug) {
        // Or throw an error, or return a promise that resolves to a default state
        return Promise.reject(new Error('Tenant slug is not available'));
      }

      return tenantControllerGetTenantSales({
        path: { tenantSlug: tenant.slug },
        query: { page, limit, ...buildSalesQuery(filters) },
        throwOnError: true,
      }).then((res) => res.data as PaginatedSales);
    },
    enabled: !!tenant.slug, // Ensure query only runs when tenant.slug is available
  });
}

/** Mobile orders list: pages of 20 with "Carregar mais" (SALES-07 AC 2). */
export function useInfiniteSales(
  values: SalesHistoryFilterValues,
  enabled = true
) {
  const tenant = useTenant();

  return useInfiniteQuery<
    PaginatedSalesResponseDto,
    Error,
    InfiniteData<PaginatedSalesResponseDto>,
    readonly unknown[],
    number
  >({
    queryKey: ['sales', tenant.slug, 'infinite', values],
    queryFn: ({ pageParam }) =>
      tenantControllerGetTenantSales({
        path: { tenantSlug: tenant.slug },
        query: {
          page: pageParam,
          limit: SALES_MOBILE_PAGE_SIZE,
          ...buildSalesQuery(values),
        },
        throwOnError: true,
      }).then((res) => res.data),
    initialPageParam: 1,
    getNextPageParam: getNextSalesPage,
    enabled: !!tenant.slug && enabled,
  });
}
